<template>
  <div class="app-shell">
    <aside class="app-sidebar" :class="{ open: sidebarOpen }">
      <RouterLink to="/" class="shell-brand" @click="sidebarOpen = false">
        <div class="shell-brand-mark">X</div>

        <div>
          <strong>CERTIFICADOR</strong>
          <small>{{ authState.company?.name || "X Solution" }}</small>
        </div>
      </RouterLink>

      <nav class="shell-nav">
        <div
          v-for="section in navigation"
          :key="section.label"
          class="shell-nav-section"
        >
          <span class="shell-nav-section-label">{{ section.label }}</span>

          <RouterLink
            v-for="item in section.items"
            :key="item.name"
            :to="{ name: item.name }"
            class="shell-nav-link"
            :class="{ disabled: item.disabled }"
            @click.prevent="handleNavigation(item)"
          >
            <component :is="item.icon" class="shell-nav-icon" />

            <span>{{ item.label }}</span>

            <small v-if="item.badge" class="shell-nav-badge">
              {{ item.badge }}
            </small>
          </RouterLink>
        </div>
      </nav>

      <div class="shell-sidebar-footer">
        <div class="shell-status-card">
          <span>Motor de assinatura</span>
          <strong>DSS/PAdES LT</strong>
          <small>ITI validado</small>
        </div>
      </div>
    </aside>

    <div
      v-if="sidebarOpen"
      class="shell-mobile-backdrop"
      @click="sidebarOpen = false"
    />

    <section class="shell-content">
      <header class="shell-topbar">
        <button
          class="shell-menu-button"
          type="button"
          @click="sidebarOpen = true"
        >
          ☰
        </button>

        <div class="shell-page-title">
          <span>{{ route.meta?.eyebrow || "Certificador" }}</span>
          <strong>{{ route.meta?.title || "Painel" }}</strong>
        </div>

        <div class="shell-topbar-actions">
          <div class="shell-env-pill">
            <span class="shell-dot"></span>
            {{ route.meta?.status || "Operacional" }}
          </div>

          <div v-if="authState.user" class="shell-user-card">
            <div>
              <span>{{ authState.user.name }}</span>
              <small>{{ authState.company?.name || "Empresa atual" }}</small>
            </div>

            <button type="button" @click="handleLogout">Sair</button>
          </div>
        </div>
      </header>

      <main class="shell-main">
        <RouterView v-slot="{ Component }">
          <Transition name="shell-fade" mode="out-in">
            <component :is="Component" />
          </Transition>
        </RouterView>
      </main>
    </section>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRoute, useRouter, RouterLink, RouterView } from "vue-router";
import {
  PhArchiveBox,
  PhCertificate,
  PhCheckSquareOffset,
  PhClockCounterClockwise,
  PhFilePdf,
  PhGearSix,
  PhHouse,
  PhLinkSimple,
  PhPenNib,
  PhPlug,
  PhShieldCheck,
  PhSquaresFour,
  PhUsersThree,
} from "@phosphor-icons/vue";
import { authState, logout } from "@/services/authService";

const route = useRoute();
const router = useRouter();
const sidebarOpen = ref(false);

const navigation = [
  {
    label: "Principal",
    items: [
      {
        name: "dashboard",
        label: "Painel",
        icon: PhHouse,
        badge: "Novo",
      },
      {
        name: "certificates.emit",
        label: "Emitir certificados",
        icon: PhCertificate,
      },
      {
        name: "certificates.generated",
        label: "Certificados gerados",
        icon: PhArchiveBox,
        badge: "Beta",
      },
    ],
  },
  {
    label: "Assinaturas",
    items: [
      {
        name: "signatures",
        label: "Assinaturas visuais",
        icon: PhPenNib,
      },
      {
        name: "signature.requests",
        label: "Solicitações por link",
        icon: PhLinkSimple,
        badge: "Em breve",
      },
      {
        name: "digital.certificates",
        label: "Certificados digitais",
        icon: PhShieldCheck,
        badge: "Beta",
      },
    ],
  },
  {
    label: "Documentos",
    items: [
      {
        name: "documents",
        label: "Assinar documentos",
        icon: PhFilePdf,
        badge: "Em breve",
      },
      {
        name: "templates",
        label: "Modelos",
        icon: PhSquaresFour,
        badge: "Em breve",
      },
    ],
  },
  {
    label: "Integrações",
    items: [
      {
        name: "integrations",
        label: "APIs e serviços",
        icon: PhPlug,
        badge: "Em breve",
      },
      {
        name: "audit",
        label: "Auditoria",
        icon: PhClockCounterClockwise,
        badge: "Em breve",
      },
    ],
  },
  {
    label: "Administração",
    items: [
      {
        name: "settings",
        label: "Configurações",
        icon: PhGearSix,
        badge: "Em breve",
      },
      {
        name: "users",
        label: "Usuários",
        icon: PhUsersThree,
        badge: "Em breve",
      },
      {
        name: "dss.status",
        label: "Status DSS",
        icon: PhCheckSquareOffset,
        badge: "Local",
      },
    ],
  },
];

async function handleLogout() {
  await logout();
  router.push("/login");
}

function handleNavigation(item) {
  sidebarOpen.value = false;

  if (item.disabled) {
    return;
  }

  router.push({ name: item.name });
}
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  color: #111827;
  background: radial-gradient(
      circle at top left,
      rgba(255, 148, 23, 0.18),
      transparent 30rem
    ),
    radial-gradient(
      circle at top right,
      rgba(15, 23, 42, 0.08),
      transparent 34rem
    ),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 50%, #e8edf5 100%);
}

.app-sidebar {
  position: sticky;
  top: 0;
  z-index: 60;
  width: 340px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 1.2rem;
  color: #f8fafc;
  background: radial-gradient(
      circle at top left,
      rgba(255, 148, 23, 0.16),
      transparent 20rem
    ),
    linear-gradient(180deg, #070b15 0%, #0f172a 58%, #111827 100%);
  border-right: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 18px 0 55px rgba(15, 23, 42, 0.22);
}

.shell-brand {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.35rem 0.25rem 1.1rem;
  color: inherit;
  text-decoration: none;
}

.shell-brand-mark {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  color: #111827;
  font-weight: 900;
  background: linear-gradient(135deg, #ffb020, #ff7300);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2) inset,
    0 14px 34px rgba(255, 115, 0, 0.32);
}

.shell-brand strong {
  font-size: 0.9rem;
  letter-spacing: 0.01em;
}

.shell-brand small {
  font-size: 0.66rem;
}

.shell-brand strong,
.shell-page-title strong,
.shell-status-card strong {
  display: block;
  font-family: "XoloniumBold", "Xolonium", sans-serif;
  letter-spacing: 0.02em;
}

.shell-brand small {
  display: block;
  margin-top: 0.1rem;
  color: #94a3b8;
  font-size: 0.72rem;
}

.shell-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.25rem;
}

.shell-nav-section {
  margin-top: 1rem;
}

.shell-nav-section-label {
  display: block;
  margin: 0 0 0.45rem 0.2rem;
  color: #f97316;
  font-size: 0.68rem;
  font-family: "XoloniumBold", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.shell-nav-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.58rem;
  min-height: 38px;
  padding: 0.55rem 0.62rem;
  border-radius: 14px;
  color: #cbd5e1;
  text-decoration: none;
  transition: 0.18s ease;
  font-size: 0.82rem;
  line-height: 1.25;
}

.shell-nav-link:hover,
.shell-nav-link.router-link-active {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.shell-nav-link.router-link-active::before {
  content: "";
  position: absolute;
  left: -0.55rem;
  width: 4px;
  height: 26px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffb020, #ff7300);
}

.shell-nav-icon {
  width: 1.05rem;
  height: 1.05rem;
  flex: 0 0 auto;
}

.shell-nav-badge {
  margin-left: auto;
  padding: 0.18rem 0.38rem;
  border-radius: 999px;
  color: #ff7a00;
  background: rgba(255, 122, 0, 0.13);
  font-size: 0.65rem;
  line-height: 1;
  white-space: nowrap;
  text-transform: uppercase;
}

.shell-sidebar-footer {
  padding-top: 1rem;
}

.shell-status-card {
  padding: 0.82rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.shell-status-card strong {
  font-size: 0.92rem;
}

.shell-status-card span,
.shell-status-card small {
  font-size: 0.66rem;
}

.shell-status-card span,
.shell-status-card small,
.shell-page-title span,
.shell-user-card small {
  color: #94a3b8;
  font-size: 0.74rem;
}

.shell-content {
  min-width: 0;
  flex: 1;
}

.shell-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  min-height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 0.5rem 1rem;
  background: rgba(248, 250, 252, 0.78);
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  backdrop-filter: blur(18px);
}

.shell-menu-button {
  display: none;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 14px;
  color: #111827;
  background: #fff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.shell-page-title strong {
  margin-top: 0.08rem;
  font-size: 0.96rem;
}

.shell-page-title span {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.shell-topbar-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.shell-env-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 40px;
  padding: 0 0.8rem;
  border-radius: 999px;
  color: #334155;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
  font-size: 0.76rem;
}

.shell-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);
}

.shell-user-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 48px;
  padding: 0.35rem 0.45rem 0.35rem 0.85rem;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.shell-user-card span {
  display: block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111827;
  font-size: 0.8rem;
  font-family: "XoloniumBold", sans-serif;
}

.shell-user-card button {
  min-height: 36px;
  padding: 0 0.85rem;
  border: 0;
  border-radius: 13px;
  color: #111827;
  background: linear-gradient(135deg, #ffb020, #ff7300);
  font-family: "XoloniumBold", sans-serif;
  cursor: pointer;
}

.shell-main {
  padding: 1rem;
}

.shell-fade-enter-active,
.shell-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.shell-fade-enter-from,
.shell-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.shell-mobile-backdrop {
  position: fixed;
  inset: 0;
  z-index: 55;
  background: rgba(15, 23, 42, 0.5);
}

@media (max-width: 1024px) {
  .app-sidebar {
    position: fixed;
    left: 0;
    transform: translateX(-105%);
    transition: transform 0.22s ease;
  }

  .app-sidebar.open {
    transform: translateX(0);
  }

  .shell-menu-button {
    display: inline-grid;
    place-items: center;
  }

  .shell-topbar {
    padding: 0.8rem 1rem;
  }

  .shell-main {
    padding: 0.1rem;
  }

  .shell-env-pill {
    display: none;
  }
}

@media (max-width: 640px) {
  .shell-user-card div {
    display: none;
  }

  .shell-main {
    padding: 0.05rem;
  }
}
</style>
